import { useMutation } from '@tanstack/react-query';
import { useDebounceFn } from 'ahooks';
import { messageToAnswerV2 } from 'components/Dialogue/components/QAPair/utils';
import { useSelectedQueryMsgIds } from 'hooks/business/im/useMessageSelect';
import { useGetMessageById } from 'hooks/business/message';
import { noop } from 'lodash-es';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { rewriteSupplementKnowledgeQuestion } from 'services/api/supplementKnowledge';
import { EMPTY_REWRITE_QUESTION_DRAFT, RewriteQuestionDraft } from './rewriteQuestionDraft';

export const ModifyDrawerContext = createContext<{
  conversation?: Conversation.Conversation;
  primaryKey: string;
  rewriteQueryKey: number;
  resetRewriteQueryKey: () => void;
  editingText: string;
  onEditingTextChange: (text: string) => void;
  rewriteQuestionDraft: RewriteQuestionDraft;
  setRewriteQuestionDraft: (draft: RewriteQuestionDraft) => void;
  resetRewriteQuestionDraft: () => void;
}>({
  conversation: undefined,
  primaryKey: '',
  rewriteQueryKey: 0,
  resetRewriteQueryKey: noop,
  editingText: '',
  onEditingTextChange: noop,
  rewriteQuestionDraft: EMPTY_REWRITE_QUESTION_DRAFT,
  setRewriteQuestionDraft: noop,
  resetRewriteQuestionDraft: noop,
});

export const ModifyDrawerProvider = (
  props: PropsWithChildren<{
    primaryKey: string;
    conversation?: Conversation.Conversation;
  }>,
) => {
  const { children, primaryKey, conversation } = props;

  const [rewriteQueryKey, setRewriteQueryKey] = useState(0);
  const selectedQueryMsgIds = useSelectedQueryMsgIds(primaryKey);
  const [editingText, setEditingText] = useState('');
  const [rewriteQuestionDraft, setRewriteQuestionDraft] = useState<RewriteQuestionDraft>(
    EMPTY_REWRITE_QUESTION_DRAFT,
  );
  const getMessageById = useGetMessageById();

  const { mutateAsync: rewriteQuestionMutation } = useMutation({
    mutationFn: rewriteSupplementKnowledgeQuestion,
  });

  const { run: fetchRewriteQuery, cancel: cancelFetchRewriteQuery } = useDebounceFn(
    async (msgIds: string[]) => {
      const messages = messageToAnswerV2(
        msgIds
          .map((id) => getMessageById(id))
          .filter((msg): msg is NonNullable<typeof msg> => !!msg),
      );

      try {
        const response = await rewriteQuestionMutation({
          questions: messages,
        });

        if (selectedQueryMsgIds?.size) {
          setRewriteQuestionDraft({
            rewriteQuestion: response.data?.data?.rewriteQuestion ?? '',
            rewriteQuestionTranslateMap: response.data?.data?.rewriteQuestionTranslateMap ?? {},
            originQuestions: messages,
          });
        }
      } catch (error) {
        console.error('rewriteSupplementKnowledgeQuestion failed', error);
      } finally {
        setRewriteQueryKey(0);
      }
    },
    {
      wait: 1000,
    },
  );

  const resetRewriteQueryKey = useCallback(() => {
    setRewriteQueryKey(0);
  }, []);

  const resetRewriteQuestionDraft = useCallback(() => {
    setRewriteQuestionDraft(EMPTY_REWRITE_QUESTION_DRAFT);
  }, []);

  useEffect(() => {
    const size = selectedQueryMsgIds?.size;

    if (size && size > 0) {
      const msgIds = Array.from(selectedQueryMsgIds);
      setRewriteQueryKey((prev) => prev + 1);
      fetchRewriteQuery(msgIds.map(([msgId]) => msgId));
      return;
    }

    resetRewriteQueryKey();
    resetRewriteQuestionDraft();
  }, [fetchRewriteQuery, resetRewriteQueryKey, resetRewriteQuestionDraft, selectedQueryMsgIds]);

  useEffect(() => {
    return () => {
      cancelFetchRewriteQuery();
    };
  }, [cancelFetchRewriteQuery]);

  const contextValue = useMemo(() => {
    return {
      conversation,
      primaryKey,
      rewriteQueryKey,
      resetRewriteQueryKey,
      editingText,
      onEditingTextChange: setEditingText,
      rewriteQuestionDraft,
      setRewriteQuestionDraft,
      resetRewriteQuestionDraft,
    };
  }, [
    conversation,
    primaryKey,
    rewriteQueryKey,
    resetRewriteQueryKey,
    editingText,
    setEditingText,
    rewriteQuestionDraft,
    resetRewriteQuestionDraft,
  ]);

  return (
    <ModifyDrawerContext.Provider value={contextValue}>{children}</ModifyDrawerContext.Provider>
  );
};

export const useModifyDrawerContext = () => {
  return useContext(ModifyDrawerContext);
};
